import { hasMatch } from "fzy.js"

class MultiSelectElement extends HTMLElement {
  connectedCallback() {
    this.detailsElement = this.querySelector("details")
    this.summaryElement = this.querySelector("summary")
    this.inputElement = this.querySelector("input[type=search]")
    this.containerElement = this.querySelector("[data-container]")

    this.detailsElement.addEventListener("toggle", this.update)
    this.inputElement.addEventListener("input", this.filterOptions)
    this.containerElement.addEventListener("change", this.updateSummary)

    this.update()
  }

  disconnectedCallback() {
    this.detailsElement.removeEventListener("toggle", this.update)
    this.inputElement.removeEventListener("input", this.filterOptions)
    this.containerElement.removeEventListener("change", this.updateSummary)

    this.update()
  }

  // Event handlers

  update = () => {
    this.toggleGlobalEventListeners()
    if (!this.isConnected) return

    this.query = ""
    this.filterOptions()
    this.updateSummary()

    if (this.open) {
      this.inputElement.focus()
    } else {
      this.sortOptions()
    }
  }

  updateSummary = () => {
    this.summary = this.open || this.selectedOptionCount ? this.selectedOptionSummary : ""
  }

  filterOptions = () => {
    const { groups, query } = this
    for (const group of groups) {
      const options = this.findOptions(group)
      for (const option of options) {
        option.hidden = query && !hasMatch(query, this.labelForOption(option))
      }
      group.hidden = query && this.allHidden(options)
    }
    this.toggleAttribute("no-results", query && this.allHidden(groups))
  }

  sortOptions = () => {
    for (const group of this.groups) {
      const options = this.findOptions(group)
      group.replaceChildren(...options.sort(this.compareOptions))
    }
  }

  // Global event handlers

  toggleGlobalEventListeners() {
    const action = this.open && this.isConnected ? "add" : "remove"
    window[`${action}EventListener`]("click", this.closeOnClickOutside)
    window[`${action}EventListener`]("focusin", this.closeOnFocusOutside)
    window[`${action}EventListener`]("keydown", this.closeOnEscapeKey)
  }

  closeOnClickOutside = (event) => {
    if (!this.contains(event.target)) this.open = false
  }

  closeOnFocusOutside = (event) => {
    if (!this.contains(event.target)) this.open = false
  }

  closeOnEscapeKey = (event) => {
    if (event.key == "Escape") {
      this.open = false
      if (this.contains(event.target)) {
        this.summaryElement.focus()
      }
    }
  }

  // Helpers

  allHidden = (elements) => elements.every(this.isHidden)

  isHidden = (element) => element.hidden

  findGroups = (root) => [...root.querySelectorAll("[role=group]")]

  findOptions = (root) => [...root.querySelectorAll("[role=option]")]

  compareOptions = (a, b) => this.sortStringForOption(a).localeCompare(this.sortStringForOption(b))

  sortStringForOption = (option) => `${this.optionIsSelected(option) ? 0 : 1}${this.labelForOption(option)}`

  optionIsSelected = (option) => Boolean(option.querySelector(":checked"))

  labelForOption = (option) => option.getAttribute("data-label") || option.textContent

  // Properties

  get open() {
    return this.detailsElement.open
  }

  set open(value) {
    this.detailsElement.open = value
  }

  get query() {
    return this.inputElement.value
  }

  set query(value) {
    this.inputElement.value = value
  }

  set summary(value) {
    this.summaryElement.textContent = value
  }

  get selectedOptionLabels() {
    return this.selectedOptions.map(this.labelForOption)
  }

  get selectedOptionSummary() {
    const count = this.selectedOptionCount
    return `${count} ${count == 1 ? this.optionDescriptionSingular : this.optionDescriptionPlural}`
  }

  get selectedOptionCount() {
    return this.containerElement.querySelectorAll(":checked").length
  }

  get selectedOptions() {
    return this.options.filter(this.optionIsSelected)
  }

  get options() {
    return this.findOptions(this.containerElement)
  }

  get groups() {
    return this.findGroups(this.containerElement)
  }

  get optionDescriptionSingular() {
    return this.getAttribute("option-description-singular")
  }

  get optionDescriptionPlural() {
    return this.getAttribute("option-description-plural")
  }
}

customElements.define("multi-select", MultiSelectElement)
